/*
 * tol-announcement.js — TOL お知らせバーの横スライドカルーセル
 * 仕様正本: 統合管理表 20260807 H-13(A)（R4）
 *   横スライド（transform translateX + transition 450ms ease）／クローン方式のシームレス
 *   無限ループ／自動再生 5s（hover・スワイプ中は停止し離れたら再開）／左右矢印（連打で
 *   破綻しない）／SP スワイプ／横移動 10px 以上でクリック無効化／prefers-reduced-motion では
 *   自動再生を止めて矢印操作のみ／role="region" aria-label 維持／aria-live なし（S13 既裁定）。
 *   **スクロール固定（sticky 化）は 2026-08-07 に中止確定＝バー非 sticky は不変**。
 *
 * DOM 契約は snippets/tol-announcement-bar.liquid のコメントが正本。
 * 意匠と「実高不変」「inset ショートハンド禁止」「px 固定」の理由は assets/tol-announcement.css。
 * custom element は作らない（Dawn 本体無改修・tol- 隔離。tol-hero.js / tol-slider.js と同規律）。
 *
 * ★この面に固有の最重要事実 ────────────────────────────────────────────
 * 同一 snippet を PC シェルと SP シェルの 2 セクションが render するため、**同じマークアップが
 * 1 ページに 2 インスタンス存在し、990px の相補 media query で片方は display:none**。
 * display:none の要素では transition が走らず transitionend が永久に来ない。よって:
 *   - 送りは **%（トラック幅基準）の translateX** で行いピクセル計測に依存しない
 *     （裏インスタンスで幅 0 でも位置が壊れない。ドラッグ中の指追従だけが px を使うが、
 *      それは可視インスタンスでしか起こらない）。
 *   - 完了検知は **setTimeout だけ**（transitionend / transitioncancel は使わない）。
 *     イベントを move に帰属させる手段がなく、連打で古い transitioncancel が新しい move を
 *     前倒しで settle してしまうため撤去した（詳細は slideTo 直前のコメント）。
 *     タイムアウトは display:none の裏インスタンスでも背景タブでも必ず来る。
 *   - 自動再生は ResizeObserver 由来の pause 理由 'hidden' と tick 冒頭の clientWidth 判定の
 *     二重防御で裏インスタンスを進めない。**tick の skip はスケジュールを解除しない**
 *     （＝自己再予約型の setTimeout チェーンではなく setInterval を使う理由。skip で予約が
 *      切れると 990px を跨いで可視化したインスタンスが恒久 stall する）。
 *   - 小数ビューポート幅（ズーム等）では両インスタンスが同時可視になりうるので、
 *     **「常に片方だけ可視」を前提にしない**（各インスタンスが独立に正しく動く）。
 *
 * ★状態機械（推測で埋めないための定義）────────────────────────────────
 *   physical : track.children 内の位置。0 = 末尾スライドのクローン / 1..N = 実スライド /
 *              N+1 = 先頭スライドのクローン。transform は常に translateX(-physical * 100%)。
 *   logical  : 論理的な現在お知らせ（0..N-1）。**a11y 更新はこれを唯一の真とする**
 *              （クローン表示中でも対応する original を current とみなす）。
 *   phase    : 'idle' | 'animating' | 'dragging'
 *   moveSeq  : 単調増加トークン。settle(seq) は seq === state.moveSeq のときだけ実行 ＝
 *              古い完了タイムアウトを無効化する唯一の条件。
 *   不変条件 : **move は常に physical 1..N から始まる**（クローン位置から move を始めない）。
 *              これが成り立つ限り両端 1 枚ずつのクローンで足りる。
 *              **瞬間移動は jump() 1 箇所だけ**（init / クローン正規化 / 即時 settle /
 *              reduced-motion / pageshow / visibilitychange の各経路で共用）。
 *              確定ドラッグ以後の全退出経路は endDrag() に合流する。
 */
(() => {
  /* 同一 src の <script> はセクション複数配置で複数回実行される（フェッチは1回・実行はタグごと）。
     bar 単位は data-tol-announcement-init で冪等だが、document レベルの多重登録を防ぐ */
  if (window.__tolAnnouncementLoaded) return;
  window.__tolAnnouncementLoaded = true;

  /* 完了タイムアウトの長さは **CSS の computed transition-duration を読んで決める**。
     JS は duration を書かない（inline で書くと author inline が media query に勝つ）。
     450 を JS 側に写し取ると CSS の値を変えたときに片方だけ動き「遷移途中で jump が入って
     スナップする」ため、写しは持たない（読めない環境向けのフォールバックだけ定数で保持）。 */
  const FALLBACK_SLIDE_MS = 450;
  const SETTLE_SAFETY_MS = 120;
  const AUTOPLAY_MS = 5000;
  /* 管理表逐語の 10px（既存 tol-slider.js:194 の 5px からの意図的乖離）。
     ドラッグ確定とクリック無効化を同一しきい値にする＝「確定したのにクリックも通る」窓を作らない */
  const DRAG_THRESHOLD = 10;
  /* コミットしきい値はスライド幅の 20%（DECISIONS 2026-08-03 決定6①・tol-pdp-gallery と同値）＋
     フリック救済（速く弾いたのに戻る、を避ける。ユーザー裁定 2026-08-18） */
  const COMMIT_RATIO = 0.2;
  const FLICK_MS = 250;

  const mq = (query) => {
    try {
      return window.matchMedia(query);
    } catch (_) {
      return null;
    }
  };
  /* Safari ≤13 は MediaQueryList.addEventListener 非対応＝ここで throw すると init が中断する
     （S13 の既知事項）。addListener へフォールバックし、少なくとも矢印は生かす */
  const onMqChange = (list, fn, signal) => {
    if (!list) return;
    try {
      list.addEventListener('change', fn, { signal });
    } catch (_) {
      try {
        list.addListener(fn);
        /* addListener 経路は signal に紐づかないので teardown を自分で配線する。
           これが無いと Safari ≤13 でエディタの unload/load を繰り返すたびに
           デタッチ済みインスタンスのコールバックが残り、reduced-motion 変更で全部発火する
           （Codex クロスレビュー LOW） */
        if (signal) {
          signal.addEventListener('abort', () => {
            try {
              list.removeListener(fn);
            } catch (_) {}
          });
        }
      } catch (_) {
        /* 監視できない環境では初期値のみで動く（劣化受容） */
      }
    }
  };

  const reduced = mq('(prefers-reduced-motion: reduce)');
  /* タッチ端末では tap の emulated mouseenter に mouseleave が来ず hover が固着しうる
     （S13 ADR が「将来 (hover:hover) ガード検討可」と申し送った事項の消化）。
     hover 由来の pause と :hover 判定の両方をこの MQ でゲートする */
  const hoverCapable = mq('(hover: hover) and (pointer: fine)');
  const hoverMatters = () => !hoverCapable || hoverCapable.matches;

  /* 1 インスタンス分の初期化。throw しても呼び出し側（init）で捕まえ、他インスタンスを止めない */
  const initBar = (bar) => {
    if (bar.dataset.tolAnnouncementInit) return;
    const viewport = bar.querySelector('[data-tol-announcement-viewport]');
    const track = bar.querySelector('[data-tol-announcement-track]');
    const originals = track ? [...track.querySelectorAll('[data-tol-announcement-slide]')] : [];
    /* 2 枚未満（static 設定 / N=1 / design_mode の 0 件プレースホルダ）は no-op。
       --ready を付けないので矢印も出ない = fail-closed */
    if (!viewport || !track || originals.length < 2) return;
    bar.dataset.tolAnnouncementInit = 'true';

    const designMode = !!(window.Shopify && window.Shopify.designMode);
    const abort = new AbortController();
    const signal = abort.signal;
    const N = originals.length;
    /* teardown ハンドルを**先に**公開する。init 途中（document 側リスナー登録の後）で throw すると、
       冒頭で立てた data-tol-announcement-init が残って再 init 不能になり、かつ
       shopify:section:unload から abort() へ到達できずデタッチ済みリスナーが残る。
       最終形は init 末尾で上書きする（go / state を含む完全版）。 */
    bar._tolAnnouncement = { abort: () => abort.abort() };

    const state = {
      physical: 1,
      logical: 0,
      phase: 'idle',
      moveSeq: 0,
      timer: null,
      settleTimer: null,
      lastArrow: null,
    };
    const pauseReasons = new Set();
    const drag = {
      active: false,
      moved: false,
      pointerId: null,
      startX: 0,
      dx: 0,
      width: 0,
      startedAt: 0,
      raf: 0,
    };
    let clickSuppress = false;
    let ro = null;

    /* ---- クローン（両端 1 枚ずつ）---- */
    const sanitize = (clone) => {
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.setAttribute('draggable', 'false');
      /* スライドマーカーを外す = 再 init しても originals 集合がクローンで汚れない
         （クローン二重生成の 1 段目の封じ手。tol-hero.js:70 と同型） */
      clone.removeAttribute('data-tol-announcement-slide');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      return clone;
    };
    const headClone = sanitize(originals[N - 1].cloneNode(true));
    const tailClone = sanitize(originals[0].cloneNode(true));

    /* ---- transform ---- */
    const transformFor = (physical, offsetPx) => {
      const pct = physical * -100;
      return offsetPx ? `translateX(calc(${pct}% + ${offsetPx}px))` : `translateX(${pct}%)`;
    };
    const applyTransform = (offsetPx) => {
      track.style.transform = transformFor(state.physical, offsetPx);
    };
    /* 唯一の「瞬間移動」。同期・同一タスク内で完結させ rAF を挟まない
       （rAF 待ちの隙間に次の move が入るとクローン位置から長距離スライドする＝決定的破綻。
        tol-header-drawer.js の rAF×2 は「遷移を待ちたい」逆向きの要求なので流用しない） */
    const jump = () => {
      track.style.transition = 'none';
      applyTransform(0);
      void track.offsetWidth; /* 強制 reflow: transition なしの新値をここで確定させる */
      track.style.removeProperty('transition'); /* CSS の値へ返す（duration は書かない） */
    };

    /* ---- a11y（現在スライドのみ露出。クローンは常に非露出） ---- */
    const moveFocusOut = () => {
      const incoming = originals[state.logical];
      /* 移し先の優先: ①新しい現在スライドがリンクならそこ ②直前に押した矢印
         ③**どの矢印でもよい**（矢印を一度も押していないドラッグ経路で null に落ちると、
         フォーカスが残ったまま aria-hidden が付く＝axe aria-hidden-focus 違反になる。
         tabindex="-1" を付けても既存のフォーカスは外れないので、ここが最後の砦）。
         blur は取らない（body へ飛ぶとキーボード位置を失う） */
      const target =
        incoming && incoming.tagName === 'A'
          ? incoming
          : state.lastArrow || bar.querySelector('.tol-announcement__arrow');
      if (!target || typeof target.focus !== 'function') return;
      try {
        target.focus({ preventScroll: true });
      } catch (_) {
        target.focus();
      }
    };
    const syncA11y = () => {
      const incoming = originals[state.logical];
      incoming.removeAttribute('aria-hidden');
      incoming.removeAttribute('tabindex');
      originals.forEach((el, i) => {
        if (i === state.logical) return;
        /* aria-hidden をフォーカス中要素の祖先にしてはならない（AT からフォーカス位置が消える）。
           実際に到達するのは「リンク上で pointerdown → ドラッグ確定 → コミット」経路。
           blur は取らない（body へ飛ぶとキーボード位置を失う） */
        if (el.contains(document.activeElement)) moveFocusOut();
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('tabindex', '-1');
      });
    };

    /* ---- 自動再生 ---- */
    const autoplayOk = () =>
      !designMode &&
      !(reduced && reduced.matches) &&
      !document.hidden &&
      bar.isConnected &&
      viewport.clientWidth > 0 &&
      !(hoverMatters() && bar.matches(':hover')) &&
      pauseReasons.size === 0;
    const stopTimer = () => {
      if (state.timer) clearInterval(state.timer);
      state.timer = null;
    };
    const tick = () => {
      /* skip はスケジュールを解除しない（setInterval を使う理由）。
         RO が N→0 を通知しない環境への二重防御でもある */
      if (viewport.clientWidth === 0 || !autoplayOk()) return;
      go(1);
    };
    const restart = () => {
      stopTimer();
      if (autoplayOk()) state.timer = setInterval(tick, AUTOPLAY_MS);
    };
    const pause = (reason) => {
      pauseReasons.add(reason);
      stopTimer();
    };
    const resume = (reason) => {
      pauseReasons.delete(reason);
      restart(); /* 離脱時は必ずフル 5 秒（tick スキップ方式の位相バグを避ける） */
    };

    /* ---- 送り ---- */
    const clearSettleTimer = () => {
      if (state.settleTimer) clearTimeout(state.settleTimer);
      state.settleTimer = null;
    };
    const normalizeClonePosition = () => {
      if (state.physical === 0) {
        state.physical = N;
        jump();
      } else if (state.physical === N + 1) {
        state.physical = 1;
        jump();
      }
    };
    const settle = (seq) => {
      if (seq !== state.moveSeq) return;
      clearSettleTimer();
      normalizeClonePosition();
      state.phase = 'idle';
      syncA11y();
      /* 自動再生が回っている最中は位相を乱さない（矢印/ドラッグ経路は呼び出し側で restart する） */
      if (!state.timer) restart();
    };
    /* 進行中の move の所有権を奪い、目標位置へ同期確定する（連打・ドラッグ開始・非表示化・
       reduced-motion 切替の共通前処理）。入力ロックもキューも持たない理由 = 450ms×N の
       入力無視／遅延追従はどちらも「破綻しない」の意図に反する側の失敗 */
    const finalizePending = () => {
      if (state.phase !== 'animating') return;
      state.moveSeq += 1;
      clearSettleTimer();
      normalizeClonePosition();
      jump();
      state.phase = 'idle';
      syncA11y();
    };
    /* CSS の transition-duration を ms で読む。**リストは全要素を見て最大値を採る**
       （先頭だけ見ると、将来 `transition-duration: 100ms, 450ms`（property が opacity, transform）
       のような普通のリスト化で「220ms で settle → transform はまだ走行中」＝この修正で消した
       遷移途中の jump が復活する。`0s, 450ms` では canAnimate() が誤って同期パスを選ぶ）。
       単位が ms/s のどちらでもない要素は無視し、1 つも読めなければフォールバックへ。 */
    const maxTimeMs = (value, floorAtZero) => {
      let max = -1;
      String(value || '')
        .split(',')
        .forEach((part) => {
          const raw = part.trim();
          let ms = NaN;
          if (raw.endsWith('ms')) ms = parseFloat(raw);
          else if (raw.endsWith('s')) ms = parseFloat(raw) * 1000;
          if (!Number.isFinite(ms)) return;
          if (floorAtZero && ms < 0) ms = 0; /* 負の delay は「途中から開始」＝待ち時間 0 と等価 */
          if (ms > max) max = ms;
        });
      return max;
    };
    /* 完了待ちの予算 = max(duration) + max(delay)。**delay も足す**のが要点で、CSS に
       transition-delay が入ると duration だけ見た予算では遷移途中に settle → 正規化の jump が
       走り、この一連の修正で消した「途中スナップ」がそのまま復活する（duration リストの
       先頭だけ見ていた欠陥の兄弟）。現行 CSS に delay は無いので予防。 */
    const cssDurationMs = () => {
      const d = maxTimeMs(getComputedStyle(track).transitionDuration, false);
      if (d < 0) return FALLBACK_SLIDE_MS;
      const delay = maxTimeMs(getComputedStyle(track).transitionDelay, true);
      return d + (delay > 0 ? delay : 0);
    };
    const canAnimate = () =>
      !(reduced && reduced.matches) && viewport.clientWidth > 0 && cssDurationMs() > 0;
    const slideTo = (physical, logical) => {
      state.moveSeq += 1;
      const seq = state.moveSeq;
      clearSettleTimer();
      state.physical = physical;
      state.logical = logical;
      const target = transformFor(physical, 0);
      /* 差分ゼロなら遷移そのものが起きない → 同期パスへ落とす（空待ちを作らない） */
      if (!canAnimate() || track.style.transform === target) {
        jump();
        settle(seq);
        return;
      }
      state.phase = 'animating';
      track.style.transform = target;
      state.settleTimer = setTimeout(() => settle(seq), cssDurationMs() + SETTLE_SAFETY_MS);
    };
    const go = (dir) => {
      finalizePending(); /* ここを通ることで move の開始位置は常に 1..N */
      slideTo(state.physical + dir, (state.logical + dir + N) % N);
    };

    /* ★完了検知は setTimeout（CSS の duration + 保険）**だけ**に一本化する。
       transitionend / transitioncancel は「早期化の最適化」として一度実装したが、
       **イベントを move に帰属させる手段がない**ため取り外した（Codex クロスレビュー HIGH）:
         連打で finalizePending() → jump() が走ると、走行中の transition がキャンセルされて
         transitioncancel が**キューに乗ったまま**新しい move が始まる。配送時には phase が
         再び 'animating' になっているので、ハンドラは新しい moveSeq で settle してしまい、
         新 move の保険 timeout を解除したうえでクローン正規化を前倒しで実行する
         （= 遷移途中で jump が入りスナップして見える）。終点だけを見るテストでは検出できない。
       イベントに generation を持たせる手当ても可能だが、タイムアウトはどの経路でも必ず来る
       （display:none の裏インスタンスでも背景タブでも）ので、失敗クラスを 1 つ丸ごと消す方を採る。
       代償は settle が視覚上の終了より最大 SETTLE_SAFETY_MS 遅れることだけ:
       クローン位置と実位置は視覚同一・aria-live なし・遅延中の入力は finalizePending が拾う。 */

    /* ---- ドラッグ / スワイプ ---- */
    const onWinMove = (e) => {
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      /* up の取りこぼし（ウィンドウ外リリース・alt+tab）で残った状態が hover 移動で
         誤確定するのを遮断（tol-pdp-gallery.js:376-381 と同型） */
      if (e.pointerType === 'mouse' && e.buttons === 0) {
        endDrag(false);
        return;
      }
      const raw = e.clientX - drag.startX;
      if (!drag.moved) {
        if (Math.abs(raw) < DRAG_THRESHOLD) return;
        drag.moved = true;
        bar.classList.add('tol-announcement__bar--dragging');
        try {
          viewport.setPointerCapture(drag.pointerId);
        } catch (_) {
          /* capture 失敗時は window リスナーで追従継続（劣化受容） */
        }
        track.style.transition = 'none';
      }
      /* ±viewport 幅にクランプ = 1 ジェスチャ最大 ±1 枚。クランプしないと 100% 超ドラッグで
         クローンより手前の「空領域」が露出する（1 端 1 クローン設計の構造的欠陥） */
      drag.dx = Math.max(-drag.width, Math.min(drag.width, raw));
      if (!drag.raf) {
        drag.raf = requestAnimationFrame(() => {
          drag.raf = 0;
          applyTransform(drag.dx);
        });
      }
    };
    const onWinUp = (e) => {
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      endDrag(true);
    };
    const onWinCancel = (e) => {
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      /* touch-action:pan-y でブラウザが縦スクロールを引き取った瞬間に来る＝SP の正常系。
         異常扱いせず元位置へ戻す */
      endDrag(false);
    };
    /* 確定ドラッグ以後の全退出経路の合流点。ここが漏れると
       「バーが途中位置で凍結＋リンクが押せない」になる */
    function endDrag(flush) {
      if (!drag.active) return;
      if (drag.raf) {
        cancelAnimationFrame(drag.raf);
        drag.raf = 0;
      }
      const moved = drag.moved;
      const dx = drag.dx;
      const width = drag.width || viewport.clientWidth || 1;
      const elapsed = performance.now() - drag.startedAt;
      const pointerId = drag.pointerId;

      window.removeEventListener('pointermove', onWinMove);
      window.removeEventListener('pointerup', onWinUp);
      window.removeEventListener('pointercancel', onWinCancel);
      try {
        if (pointerId !== null && viewport.hasPointerCapture && viewport.hasPointerCapture(pointerId)) {
          viewport.releasePointerCapture(pointerId);
        }
      } catch (_) {}

      drag.active = false;
      drag.moved = false;
      drag.pointerId = null;
      drag.dx = 0;
      bar.classList.remove('tol-announcement__bar--dragging');
      state.phase = 'idle';

      if (moved) {
        clickSuppress = true;
        /* 保険解除。pointerup 由来の click は同一 input タスク内でタイマーより先に配送される
           （現行主要エンジンの実装挙動）。click が発火しないケースでフラグが残り、
           次の正当クリックを食うのを防ぐ */
        setTimeout(() => {
          clickSuppress = false;
        }, 0);
        /* 最終 dx を同期 flush → 強制 reflow → transition 復帰。この順序でないと
           アニメの開始値が drag 前の値になり、指の位置から飛ぶ */
        applyTransform(dx);
        void track.offsetWidth;
        track.style.removeProperty('transition');
      }

      resume('pointer');
      if (!moved) return;

      const commit =
        flush &&
        (Math.abs(dx) > width * COMMIT_RATIO || (elapsed < FLICK_MS && Math.abs(dx) >= DRAG_THRESHOLD));
      if (commit) {
        go(dx < 0 ? 1 : -1);
      } else {
        slideTo(state.physical, state.logical); /* 元位置へアニメで戻す */
      }
      restart();
    }

    viewport.addEventListener(
      'pointerdown',
      (e) => {
        if (!e.isPrimary) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (viewport.clientWidth === 0) return;
        if (drag.active) endDrag(false); /* 前回の取りこぼしはここで巻き戻す */
        finalizePending(); /* 進行中 move の所有権を奪う */
        drag.pointerId = e.pointerId;
        drag.startX = e.clientX;
        drag.dx = 0;
        drag.width = viewport.clientWidth;
        drag.startedAt = performance.now();
        drag.moved = false;
        drag.active = true;
        state.phase = 'dragging';
        pause('pointer');
        /* preventDefault しない = しきい値未満の微動は通常クリックとして生きる。
           確定前にトラック外へ出ても up/cancel を取りこぼさないよう window で購読 */
        window.addEventListener('pointermove', onWinMove, { signal });
        window.addEventListener('pointerup', onWinUp, { signal });
        window.addEventListener('pointercancel', onWinCancel, { signal });
      },
      { signal }
    );
    viewport.addEventListener(
      'lostpointercapture',
      () => {
        if (drag.active) endDrag(false);
      },
      { signal }
    );
    /* スライドは <a> なのでネイティブのリンクドラッグが始まると pointer stream が死ぬ */
    viewport.addEventListener(
      'dragstart',
      (e) => {
        if (drag.active) e.preventDefault();
      },
      { signal }
    );
    /* ドラッグ直後のリンク誤発火を capture で抑止。**viewport にだけ掛ける**ので
       viewport の外にある矢印の click は決して食わない（この DOM 構造の設計意図） */
    viewport.addEventListener(
      'click',
      (e) => {
        if (!clickSuppress) return;
        e.preventDefault();
        e.stopPropagation();
        clickSuppress = false;
      },
      { capture: true, signal }
    );

    /* ---- 矢印 ---- */
    const bindArrow = (el, dir) => {
      if (!el) return;
      el.addEventListener(
        'click',
        () => {
          state.lastArrow = el;
          go(dir);
          /* 手動操作後も自動再生は継続する（管理表「離れたら再開」に揃える・
             tol-hero からの暗黙継承ではなく R4 の明示裁定） */
          restart();
        },
        { signal }
      );
    };
    bindArrow(bar.querySelector('[data-tol-announcement-prev]'), -1);
    bindArrow(bar.querySelector('[data-tol-announcement-next]'), 1);

    /* ---- pause 理由の配線 ---- */
    bar.addEventListener(
      'mouseenter',
      () => {
        if (hoverMatters()) pause('hover');
      },
      { signal }
    );
    bar.addEventListener('mouseleave', () => resume('hover'), { signal });
    bar.addEventListener('focusin', () => pause('focus'), { signal });
    bar.addEventListener(
      'focusout',
      (e) => {
        if (!bar.contains(e.relatedTarget)) resume('focus');
      },
      { signal }
    );
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          /* 背景タブでは setTimeout がクランプされる → 「非表示の間は保留状態を残さない」。
             ★ドラッグ中は finalizePending() が phase==='dragging' で早期 return するため
             何も起きない＝pause 理由 'pointer' と inline transition:none と半端な transform が
             残り、復帰後に自動再生が恒久停止する。全退出経路は endDrag() に合流させる */
          stopTimer();
          if (drag.active) endDrag(false);
          finalizePending();
        } else {
          jump(); /* べき等な自己正規化 */
          restart();
        }
      },
      { signal }
    );
    onMqChange(
      reduced,
      () => {
        finalizePending();
        restart();
      },
      signal
    );

    /* 表示/非表示（990px の帯切替）を pause 理由として出し入れする。
       判定は clientWidth === 0（tol-slider.js:61-65 の前例）。offsetParent は
       visibility:hidden / content-visibility を「表示」と誤判定しうるので使わない */
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(() => {
        if (viewport.clientWidth === 0) {
          pause('hidden');
          /* ★指を置いたまま 990px 境界を跨いだ（端末回転等）経路。display:none は DOM からの
             除去ではないので pointercancel / lostpointercapture が来る保証がない（仕様上）＝
             endDrag() を明示的に通さないとドラッグ状態が残り、再表示後に自動再生が
             恒久停止する（pause 理由 'pointer' が消えない） */
          if (drag.active) endDrag(false);
          finalizePending();
        } else if (pauseReasons.has('hidden')) {
          resume('hidden'); /* 可視化した瞬間に新しい 5 秒で再アーム（出た直後に切り替わらない） */
        }
      });
      ro.observe(viewport);
    }

    /* bfcache 復帰: 復元 DOM には JS 生成クローンと inline transform も含まれる。
       凍結中だったタイマー・保留遷移・ドラッグ状態・click 抑止フラグを巻き戻す */
    window.addEventListener(
      'pageshow',
      (e) => {
        if (!e.persisted) return;
        state.moveSeq += 1;
        clearSettleTimer();
        stopTimer();
        endDrag(false);
        clickSuppress = false;
        normalizeClonePosition();
        jump();
        state.phase = 'idle';
        syncA11y();
        restart();
      },
      { signal }
    );

    /* ---- teardown（エディタのセクション削除/再レンダ・同一ノード再利用でも冪等） ---- */
    const teardown = () => {
      abort.abort();
      stopTimer();
      clearSettleTimer();
      if (drag.raf) cancelAnimationFrame(drag.raf);
      if (ro) ro.disconnect();
      try {
        if (drag.pointerId !== null && viewport.hasPointerCapture && viewport.hasPointerCapture(drag.pointerId)) {
          viewport.releasePointerCapture(drag.pointerId);
        }
      } catch (_) {}
      headClone.remove();
      tailClone.remove();
      track.style.removeProperty('transform');
      track.style.removeProperty('transition');
      bar.classList.remove('tol-announcement__bar--ready');
      bar.classList.remove('tol-announcement__bar--dragging');
      delete bar.dataset.tolAnnouncementInit;
    };
    bar._tolAnnouncement = { abort: teardown, go, state };

    /* ---- init（クローン挿入 → 初期 transform を 1 タスク内で同期実行） ----
       head クローンを prepend すると実スライド 1 は位置 1 になる。transition が載った状態で
       初期 transform を書くと 450ms アニメして「横から滑り込む」ので jump() で確定させる */
    track.insertBefore(headClone, originals[0]);
    track.appendChild(tailClone);
    state.physical = 1;
    state.logical = 0;
    jump();
    syncA11y();
    bar.classList.add('tol-announcement__bar--ready'); /* 矢印はここで初めて表示される */
    restart();
  };

  const init = (root) => {
    root.querySelectorAll('[data-tol-announcement]').forEach((bar) => {
      try {
        initBar(bar);
      } catch (err) {
        /* 1 本目（PC）で throw しても 2 本目（SP）の初期化を止めない＝「片方だけ fail-closed」に
           劣化させる。中断した側は --ready が付かないので矢印が出ず 1 件目の静止表示になる。
           完全に無音だと「矢印が出ない」原因が実機で追えないため、エディタでだけ理由を出す */
        if (window.Shopify && window.Shopify.designMode && window.console) {
          console.warn('[tol-announcement] init failed; bar stays static', err);
        }
      }
    });
  };

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-tol-announcement]').forEach((bar) => {
      if (bar._tolAnnouncement) bar._tolAnnouncement.abort();
    });
  });
})();
